import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { AppSidebar } from '../components/app-sidebar';
import { NotificationIcon } from '../components/notification-icon';
import { ProtectedRoute } from '../components/protected-route';
import { SetupModal } from '../components/setup/setup-modal';
import { YearSelect } from '../components/year-select';
import { type OrganizationData, useOrganization } from '../contexts/organization-context';
import { useAuth } from '../hooks/use-auth';
import { useCanSeeAdmin } from '../hooks/use-can-see-admin';
import { adminApi, type NotificationItem, notificationsApi } from '../lib/api-client';
import { extractNotificationPayload } from '../lib/notifications';
import { queryKeys, STALE_TIMES } from '../lib/query-keys';
import { formatRelativeTime } from '../lib/time';
import { setOrganization } from '../stores/org-store';

export const Route = createFileRoute('/$orgPid')({
  component: ProtectedLayout,
});

function OrgSyncGuard({ children }: { children: React.ReactNode }) {
  const { orgPid } = Route.useParams();
  const { organization, organizations } = useOrganization();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const hasRedirected = useRef(false);
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const targetOrg = organizations.find((o) => o.pid === orgPid);

  // Super admins can view any org via URL even if it's not in their own
  // membership list — fetch the company so we can populate the org store.
  const adminFetchEnabled = isSuperAdmin && !targetOrg && !!orgPid;
  const { data: adminOrg } = useQuery({
    queryKey: queryKeys.admin.companyDetail(orgPid),
    queryFn: () => adminApi.companyDetail(orgPid),
    enabled: adminFetchEnabled,
    staleTime: STALE_TIMES.MEDIUM,
  });

  const resolvedTargetOrg = targetOrg ?? (isSuperAdmin && adminOrg ? adminOrg : null);

  // Only redirect when a non-super-admin is on a URL for an org they don't
  // belong to. For super admins we accept any org pid; if the lookup fails
  // the admin endpoint will surface the error.
  const isInvalidOrg = !targetOrg && !isSuperAdmin && organizations.length > 0;

  useEffect(() => {
    if (!isInvalidOrg) {
      hasRedirected.current = false;
      return;
    }
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    const fallbackOrg = organization ?? organizations[0]!;
    const subPath = location.pathname.replace(/^\/[^/]+/, '');
    void navigate({
      to: `/$orgPid${subPath || '/dashboard'}`,
      params: { orgPid: fallbackOrg.pid },
      search: location.search,
      replace: true,
    });
  }, [isInvalidOrg, organization, organizations, location, navigate]);

  // Sync org store from URL param.
  useEffect(() => {
    if (!resolvedTargetOrg) return;
    if (organization?.pid === resolvedTargetOrg.pid) return;

    if (targetOrg) {
      setOrganization(targetOrg);
    } else if (adminOrg) {
      setOrganization({
        pid: adminOrg.pid,
        name: adminOrg.name,
        slug: adminOrg.slug,
        seasonRegion: adminOrg.seasonRegion as OrganizationData['seasonRegion'],
        setupCompletedAt: adminOrg.setupCompletedAt,
      });
    }
  }, [resolvedTargetOrg, organization?.pid, targetOrg, adminOrg]);

  if (isInvalidOrg) return null;

  // While the super-admin org lookup is in flight, render nothing rather than
  // children with a stale org store from another firm.
  if (!targetOrg && isSuperAdmin && !adminOrg) return null;

  return <>{children}</>;
}

function ProtectedLayout() {
  const { orgPid } = Route.useParams();
  const { organization, organizations } = useOrganization();
  const { user } = useAuth();
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];
  const pageTitle =
    lastMatch?.pathname
      ?.split('/')
      .filter(Boolean)
      .slice(1) // skip orgPid segment
      .pop()
      ?.replace(/-/g, ' ')
      .replace(/\b\w/g, (c: string) => c.toUpperCase()) ?? 'Dashboard';

  const canSeeAdmin = useCanSeeAdmin();
  // Suppress the setup modal for super admins viewing an org they don't
  // belong to — they're inspecting, not running the owner's setup flow.
  //
  // Also suppress it for plain members: the setup endpoints are owner/admin
  // only, so a member would be held behind a blocking modal where every call
  // 403s. Connecting the charter account is not their job anyway — before this
  // they could enter the organization's MMK API key.
  const isMemberOfThisOrg = organizations.some((o) => o.pid === orgPid);
  const needsSetup =
    organization != null &&
    organization.setupCompletedAt == null &&
    canSeeAdmin &&
    (isMemberOfThisOrg || !user?.isSuperAdmin);

  return (
    <ProtectedRoute>
      <OrgSyncGuard>
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset className="min-w-0 overflow-hidden">
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
              <div className="flex items-center gap-2 px-4">
                <SidebarTrigger className="-ml-1" />
                <Separator
                  orientation="vertical"
                  className="mr-2 data-[orientation=vertical]:h-4"
                />
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbPage>{pageTitle}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </div>
              <div className="ml-auto flex items-center gap-2 px-4">
                <YearSelect />
                <NotificationBell />
              </div>
            </header>
            <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 pt-0">
              <Outlet />
            </div>
          </SidebarInset>
          {needsSetup && (
            <div className="fixed inset-0 z-50 backdrop-blur-sm bg-background/80">
              <SetupModal orgPid={orgPid} />
            </div>
          )}
        </SidebarProvider>
      </OrgSyncGuard>
    </ProtectedRoute>
  );
}

function NotificationBell() {
  const { orgPid } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: unreadData } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(orgPid),
    queryFn: () => notificationsApi.unreadCount(),
    enabled: !!orgPid,
    staleTime: STALE_TIMES.SHORT,
  });

  const { data: recentData } = useQuery({
    queryKey: queryKeys.notifications.recent(orgPid),
    queryFn: () => notificationsApi.list({ limit: 5 }),
    enabled: !!orgPid,
    staleTime: STALE_TIMES.SHORT,
  });

  const markReadMutation = useMutation({
    mutationFn: (pid: string) => notificationsApi.markRead(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  const count = unreadData?.count ?? 0;
  const recent = recentData?.data ?? [];

  function handleItemClick(notification: NotificationItem) {
    if (!notification.readAt) {
      markReadMutation.mutate(notification.id);
    }
    const { compSetPid } = extractNotificationPayload(notification);
    if (compSetPid) {
      void navigate({ to: '/$orgPid/competitor-sets/$pid', params: { orgPid, pid: compSetPid } });
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="relative inline-flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Bell className="size-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
              {count > 99 ? '99' : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {count > 0 && (
            <span className="text-muted-foreground text-xs font-normal">{count} unread</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {recent.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No notifications yet
          </div>
        ) : (
          recent.map((notification) => {
            const { ruleType, message } = extractNotificationPayload(notification);
            const isUnread = notification.readAt === null;

            return (
              <DropdownMenuItem
                key={notification.id}
                className="flex cursor-pointer items-start gap-3 p-3"
                onClick={() => handleItemClick(notification)}
              >
                <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <NotificationIcon ruleType={ruleType} />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`line-clamp-2 text-sm ${isUnread ? 'font-medium' : 'text-muted-foreground'}`}
                  >
                    {message}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                </div>
                {isUnread && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-blue-500" />}
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="justify-center">
          <Link
            to="/$orgPid/activity"
            params={{ orgPid }}
            className="w-full text-center text-sm font-medium"
          >
            View all activity
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
